import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshClientAvatar } from '../services/api.ts';

const avatarResults = new Map<string, string | null>();
const avatarRequests = new Map<string, Promise<string | null>>();

const normalizePhoto = (value?: string | null): string | null => {
  const photo = String(value || '').trim();
  if (photo.startsWith('storage://atendabot-client-avatars/')) return null;
  return photo || null;
};

const requestAvatar = (clienteId: string | number, force: boolean): Promise<string | null> => {
  const key = String(clienteId);
  if (!force && avatarResults.has(key)) {
    return Promise.resolve(avatarResults.get(key) ?? null);
  }
  const pending = avatarRequests.get(key);
  if (pending) return pending;

  if (force) avatarResults.delete(key);
  const request = refreshClientAvatar(clienteId, force)
    .then((photo) => {
      const normalized = normalizePhoto(photo);
      avatarResults.set(key, normalized);
      return normalized;
    })
    .finally(() => {
      avatarRequests.delete(key);
    });
  avatarRequests.set(key, request);
  return request;
};

type UseClientAvatarOptions = {
  clienteId?: string | number | null;
  photo?: string | null;
  onPhotoChange?: (photo: string | null) => void;
};

export const getAvatarInitial = (name?: string | null): string => {
  const normalized = String(name || '').trim();
  return normalized ? Array.from(normalized)[0].toLocaleUpperCase('pt-BR') : 'C';
};

export const useClientAvatar = ({ clienteId, photo, onPhotoChange }: UseClientAvatarOptions) => {
  const canRefresh = clienteId !== null
    && clienteId !== undefined
    && clienteId !== ''
    && !String(clienteId).startsWith('lp-');
  const normalizedPhoto = normalizePhoto(photo);
  const cachedPhoto = clienteId == null ? undefined : avatarResults.get(String(clienteId));
  const [resolvedPhoto, setResolvedPhoto] = useState<string | null>(cachedPhoto ?? normalizedPhoto);
  const sourcePhotoRef = useRef(normalizedPhoto);
  const onPhotoChangeRef = useRef(onPhotoChange);

  useEffect(() => {
    onPhotoChangeRef.current = onPhotoChange;
  }, [onPhotoChange]);

  useEffect(() => {
    const nextPhoto = normalizePhoto(photo);
    if (sourcePhotoRef.current === nextPhoto) return;
    sourcePhotoRef.current = nextPhoto;
    setResolvedPhoto(nextPhoto);
  }, [photo]);

  const refresh = useCallback(async (force: boolean) => {
    if (!canRefresh) return null;
    try {
      const nextPhoto = await requestAvatar(clienteId, force);
      setResolvedPhoto(nextPhoto);
      onPhotoChangeRef.current?.(nextPhoto);
      return nextPhoto;
    } catch {
      setResolvedPhoto(null);
      onPhotoChangeRef.current?.(null);
      return null;
    }
  }, [canRefresh, clienteId]);

  useEffect(() => {
    if (!resolvedPhoto && canRefresh) {
      void refresh(false);
    }
  }, [canRefresh, refresh, resolvedPhoto]);

  const handleImageError = useCallback(() => {
    setResolvedPhoto(null);
    void refresh(true);
  }, [refresh]);

  return {
    photo: resolvedPhoto,
    handleImageError,
    refresh,
  };
};

export const __resetClientAvatarCacheForTests = () => {
  avatarResults.clear();
  avatarRequests.clear();
};
